import { Card, DataState, PageHeader, StatCard } from "@vms/ui-web";
import { useQuery } from "../../api/queries";

interface HubRow {
  hubId: string;
  depth: number;
  oldestPendingAgeMs: number | null;
  venueName: string | null;
  hubVersion: string | null;
  lastSyncCursor: string | null;
  connected: boolean;
  queueDepth: number;
  deferredAcks: number;
}

interface SyncStatsPayload {
  totalUnapplied: number;
  hubs: HubRow[];
  dlqCount: number;
  oldestPendingAgeMs: number | null;
  appliesPerMin: number;
  acksPerMin: number;
  connectedHubs: number;
  config: { workers: number; hubDepthCap: number; hubDepthHardCap: number };
}

function formatAge(ms: number | null): string {
  if (ms === null) return "—";
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  const h = Math.floor(m / 60);
  return `${h}h ${m % 60}m`;
}

/** Depth alone can't distinguish "50 rows, 2s old" from "50 rows stuck for an
 *  hour" — tone on age, not just depth. */
function hubTone(h: HubRow, depthCap: number): string {
  if (h.depth >= depthCap || (h.oldestPendingAgeMs ?? 0) > 5 * 60_000) return "text-red-700";
  if (h.depth > depthCap / 2 || (h.oldestPendingAgeMs ?? 0) > 60_000) return "text-amber-700";
  return "text-gray-900";
}

export function SyncHealth() {
  const stats = useQuery<SyncStatsPayload>("/api/admin/sync-stats", { refetchIntervalMs: 5000 });
  const data = stats.data;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Sync Health"
        subtitle="Hub → cloud ingestion pipeline: per-hub backlog, apply throughput, backpressure."
      />

      <DataState loading={stats.loading} error={stats.error}>
        {data && (
          <>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
              <StatCard label="Hubs connected" value={data.connectedHubs} />
              <StatCard
                label="Unapplied deltas"
                value={data.totalUnapplied}
                tone={data.totalUnapplied >= data.config.hubDepthCap ? "red" : data.totalUnapplied > 0 ? "amber" : "default"}
                hint={`cap ${data.config.hubDepthCap}/hub`}
              />
              <StatCard label="Applies / min" value={data.appliesPerMin} hint={`${data.config.workers} workers`} />
              <StatCard label="Acks / min" value={data.acksPerMin} />
              <StatCard
                label="Dead-lettered"
                value={data.dlqCount}
                tone={data.dlqCount > 0 ? "red" : "default"}
              />
              <StatCard
                label="Oldest pending"
                value={formatAge(data.oldestPendingAgeMs)}
                tone={(data.oldestPendingAgeMs ?? 0) > 5 * 60_000 ? "red" : (data.oldestPendingAgeMs ?? 0) > 60_000 ? "amber" : "default"}
              />
            </div>

            <Card className="overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <h2 className="font-semibold text-gray-900">Hubs</h2>
                <div className="text-xs text-gray-400">
                  auto-refreshes every 5s · ack backpressure at {data.config.hubDepthCap}, hard drop at {data.config.hubDepthHardCap}
                </div>
              </div>
              {data.hubs.length === 0 ? (
                <div className="px-5 py-6 text-sm text-gray-500">No hubs have connected yet.</div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                        <th className="px-5 py-2 font-medium">Venue</th>
                        <th className="px-3 py-2 font-medium">Hub</th>
                        <th className="px-3 py-2 font-medium text-right">Backlog</th>
                        <th className="px-3 py-2 font-medium text-right">Oldest</th>
                        <th className="px-3 py-2 font-medium text-right">Inbound queue</th>
                        <th className="px-3 py-2 font-medium text-right">Withheld acks</th>
                        <th className="px-5 py-2 font-medium">Cursor</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-50">
                      {data.hubs.map((h) => (
                        <tr key={h.hubId}>
                          <td className="px-5 py-2">
                            <div className="font-medium text-gray-900">{h.venueName ?? "—"}</div>
                            <div className="text-xs text-gray-400 font-mono">{h.hubId.slice(0, 8)}…</div>
                          </td>
                          <td className="px-3 py-2">
                            <span
                              className={`inline-flex items-center gap-1.5 text-xs ${h.connected ? "text-emerald-700" : "text-gray-400"}`}
                            >
                              <span
                                className={`w-1.5 h-1.5 rounded-full ${h.connected ? "bg-emerald-500" : "bg-gray-300"}`}
                              />
                              {h.connected ? "online" : "offline"}
                            </span>
                            <div className="text-xs text-gray-400">{h.hubVersion ? `v${h.hubVersion}` : ""}</div>
                          </td>
                          <td className={`px-3 py-2 text-right font-mono ${hubTone(h, data.config.hubDepthCap)}`}>
                            {h.depth}
                          </td>
                          <td className={`px-3 py-2 text-right ${hubTone(h, data.config.hubDepthCap)}`}>
                            {formatAge(h.oldestPendingAgeMs)}
                          </td>
                          <td className="px-3 py-2 text-right font-mono text-gray-600">{h.queueDepth}</td>
                          <td className={`px-3 py-2 text-right font-mono ${h.deferredAcks > 0 ? "text-amber-700" : "text-gray-600"}`}>
                            {h.deferredAcks}
                          </td>
                          <td className="px-5 py-2 text-xs text-gray-400 font-mono">{h.lastSyncCursor ?? "—"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </Card>
          </>
        )}
      </DataState>
    </div>
  );
}
